import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { authApi, Player, Wallet, RegisterData } from '../services/api';

// Rol tipleri
export type UserRole = 'player' | 'vip';

// Cihaz bilgisi tipi
export interface DeviceInfo {
  browser: string;
  browserVersion: string;
  os: string;
  osVersion: string;
  device: string;
  isMobile: boolean;
}

// Cihaz bilgisini algılayan yardimci fonksiyon
export const getDeviceInfo = (): DeviceInfo => {
  const ua = navigator.userAgent;

  // Tarayici algilama
  let browser = 'Unknown';
  let browserVersion = '';

  if (ua.includes('Firefox/')) {
    browser = 'Firefox';
    browserVersion = ua.match(/Firefox\/(\d+\.\d+)/)?.[1] || '';
  } else if (ua.includes('Edg/')) {
    browser = 'Edge';
    browserVersion = ua.match(/Edg\/(\d+\.\d+)/)?.[1] || '';
  } else if (ua.includes('Chrome/')) {
    browser = 'Chrome';
    browserVersion = ua.match(/Chrome\/(\d+\.\d+)/)?.[1] || '';
  } else if (ua.includes('Safari/') && !ua.includes('Chrome')) {
    browser = 'Safari';
    browserVersion = ua.match(/Version\/(\d+\.\d+)/)?.[1] || '';
  } else if (ua.includes('Opera/') || ua.includes('OPR/')) {
    browser = 'Opera';
    browserVersion = ua.match(/(?:Opera|OPR)\/(\d+\.\d+)/)?.[1] || '';
  }

  // Isletim sistemi algilama
  let os = 'Unknown';
  let osVersion = '';

  if (ua.includes('Windows NT')) {
    os = 'Windows';
    const ntVersion = ua.match(/Windows NT (\d+\.\d+)/)?.[1];
    if (ntVersion === '10.0') osVersion = '10/11';
    else if (ntVersion === '6.3') osVersion = '8.1';
    else if (ntVersion === '6.2') osVersion = '8';
    else if (ntVersion === '6.1') osVersion = '7';
  } else if (ua.includes('Mac OS X')) {
    os = 'macOS';
    osVersion = ua.match(/Mac OS X (\d+[._]\d+)/)?.[1]?.replace('_', '.') || '';
  } else if (ua.includes('Linux')) {
    os = 'Linux';
  } else if (ua.includes('Android')) {
    os = 'Android';
    osVersion = ua.match(/Android (\d+\.\d+)/)?.[1] || '';
  } else if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) {
    os = 'iOS';
    osVersion = ua.match(/OS (\d+_\d+)/)?.[1]?.replace('_', '.') || '';
  }

  // Cihaz turu algilama
  let device = 'Desktop';
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);

  if (ua.includes('iPad')) {
    device = 'iPad';
  } else if (ua.includes('iPhone')) {
    device = 'iPhone';
  } else if (ua.includes('Android')) {
    if (ua.includes('Mobile')) {
      device = 'Android Phone';
    } else {
      device = 'Android Tablet';
    }
  } else if (isMobile) {
    device = 'Mobile';
  }

  return {
    browser,
    browserVersion,
    os,
    osVersion,
    device,
    isMobile
  };
};

interface SarLabAuthContextType {
  currentPlayer: Player | null;
  wallet: Wallet | null;
  loading: boolean;
  register: (data: RegisterData) => Promise<{ success: boolean; error?: string }>;
  login: (emailOrUsername: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  refreshPlayerData: () => Promise<void>;
  isLoggedIn: boolean;
}

const SarLabAuthContext = createContext<SarLabAuthContextType>({} as SarLabAuthContextType);

export const useSarLabAuth = () => {
  return useContext(SarLabAuthContext);
};

interface SarLabAuthProviderProps {
  children: ReactNode;
}

export const SarLabAuthProvider: React.FC<SarLabAuthProviderProps> = ({ children }) => {
  const [currentPlayer, setCurrentPlayer] = useState<Player | null>(null);
  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [loading, setLoading] = useState(true);

  // Check if user is logged in on mount (via cookie-based session)
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const result = await authApi.me();
        if (result.success && result.data) {
          setCurrentPlayer(result.data.player || null);
          setWallet(result.data.wallet || null);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Kullanici kaydi
  const register = async (data: RegisterData): Promise<{ success: boolean; error?: string }> => {
    try {
      const result = await authApi.register(data);

      if (result.success && result.data) {
        setCurrentPlayer(result.data.player || null);
        setWallet(result.data.wallet || null);
        return { success: true };
      }

      return { success: false, error: result.error || 'Kayit islemi basarisiz' };
    } catch (error: unknown) {
      console.error('Register error:', error);
      const err = error as Error;
      return { success: false, error: err.message || 'Kayit islemi sirasinda bir hata olustu' };
    }
  };

  // Kullanici girisi
  const login = async (emailOrUsername: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const result = await authApi.login({ emailOrUsername, password });

      if (result.success && result.data) {
        setCurrentPlayer(result.data.player || null);
        setWallet(result.data.wallet || null);
        return { success: true };
      }

      return { success: false, error: result.error || 'Giris basarisiz' };
    } catch (error: unknown) {
      console.error('Login error:', error);
      const err = error as Error;
      return { success: false, error: err.message || 'Giris yapilirken bir hata olustu' };
    }
  };

  // Kullanici cikisi
  const logout = async () => {
    try {
      await authApi.logout();
      setCurrentPlayer(null);
      setWallet(null);
    } catch (error: unknown) {
      console.error('Logout error:', error);
    }
  };

  // Kullanici verilerini yenile
  const refreshPlayerData = useCallback(async () => {
    try {
      const result = await authApi.me();
      if (result.success && result.data) {
        setCurrentPlayer(result.data.player || null);
        setWallet(result.data.wallet || null);
      }
    } catch (error) {
      console.error('Failed to refresh player data:', error);
    }
  }, []);

  const value = {
    currentPlayer,
    wallet,
    loading,
    register,
    login,
    logout,
    refreshPlayerData,
    isLoggedIn: !!currentPlayer,
  };

  return (
    <SarLabAuthContext.Provider value={value}>
      {!loading && children}
    </SarLabAuthContext.Provider>
  );
};
